import React, { useContext } from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { useTranslation } from 'react-i18next'
import DataSkeleton from '@/pages/analytics/ZtSubData/dataSkeleton'
import { Button, Skeleton } from 'antd'
import { textCsatMapping } from '../config'
import PopoverItem from '@/pages/analytics/Overview/popoverTooltip'
import { useExport, TiktokContext } from '../request'
import { formatValue } from '@/utils'

echarts.registerTheme('theme_jekka', {
	backgroundColor: '#fff'
})

const SatisfiedTrend: React.FC = () => {
	const { trendData: data, loading } = useContext(TiktokContext)
	const { t } = useTranslation()

	const { exportLoading, handleExport } = useExport(11)
	const summary = data?.summary || {}

	const chartData = data?.list?.reduce(
		(acc: any, item: any) => {
			acc.nativeDate.push(item.stat_date)
			acc.date.push(item.stat_date.slice(5).replace('-', '/'))

			acc.avgSatisfactionScore.push(
				item.avg_satisfaction_score === '--' ? null : Number(item.avg_satisfaction_score)
			)

			acc.satisfactionRate.push(
				item.satisfaction_rate === '--' ? null : Number(item.satisfaction_rate)
			)

			acc.lowScoreRate.push(item.low_score_rate === '--' ? null : Number(item.low_score_rate))

			return acc
		},
		{
			date: [] as string[],
			nativeDate: [] as string[],
			avgSatisfactionScore: [] as (number | null)[],
			satisfactionRate: [] as (number | null)[],
			lowScoreRate: [] as (number | null)[]
		}
	) || {
		date: [],
		nativeDate: [],
		avgSatisfactionScore: [],
		satisfactionRate: [],
		lowScoreRate: []
	}

	const optionList = {
		tooltip: {
			trigger: 'axis',
			confine: true,
			extraCssText: 'max-width: 450px; white-space: normal;',
			formatter: (params: any) => {
				if (!params || params.length === 0) return ''
				const idx = params[0].dataIndex
				const date = chartData.nativeDate[idx]
				const lines: string[] = []

				lines.push(
					`<div style="margin-bottom:8px;">
                        <div style="margin-bottom:8px;font-weight:bold;">${t(
													'a_s_satisfaction_title_1'
												)}：<span style="font-weight:normal;">${date}</span></div>`
				)

				params.forEach((item: any) => {
					let value: any
					let labelText: any

					if (item.seriesType === 'bar') {
						const score = chartData.avgSatisfactionScore[idx]
						value = score !== null ? score : 0
						labelText = t('c_a_satisfaction_level')
					} else if (item.seriesType === 'line') {
						const rate = chartData.satisfactionRate[idx]
						value = rate !== null ? `${Math.round(rate)}%` : '0%'
						labelText = t('a_s_satisfaction_rate')
					}

					lines.push(
						`<div style="margin-bottom:6px; display:flex; align-items:center;">` +
							`<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${item.color};margin-right:6px;"></span>` +
							`<span>${labelText}：</span>` +
							`<span style="font-weight:bold;">${value}</span>` +
							`</div>`
					)
				})

				return lines.join('')
			}
		},

		legend: {
			data: [t('c_a_satisfaction_level'), t('a_s_satisfaction_rate')],
			top: 10
		},
		grid: {
			left: 10,
			right: 10,
			bottom: 12,
			top: 70,
			containLabel: true
		},

		xAxis: {
			type: 'category',
			data: chartData.date
		},
		yAxis: [
			{
				type: 'value',
				position: 'left',
				min: 0,
				max: 5,
				interval: 1
			},
			{
				type: 'value',
				position: 'right',
				min: 0,
				max: 100,
				interval: 20,
				axisLabel: {
					formatter: (value: number) => `${Math.round(value)}%`
				},
				splitLine: { show: false }
			}
		],

		series: [
			{
				name: t('c_a_satisfaction_level'),
				type: 'bar',
				data: chartData.avgSatisfactionScore,
				itemStyle: { color: '#ffb72b', barBorderRadius: [8, 8, 0, 0] },
				label: {
					show: chartData.avgSatisfactionScore?.length < 18,
					color: '#fff'
				},
				barWidth: chartData.avgSatisfactionScore?.length < 10 ? 36 : 'auto',
				yAxisIndex: 0
			},
			{
				name: t('a_s_satisfaction_rate'),
				type: 'line',
				data: chartData.satisfactionRate,
				itemStyle: { color: '#8B5CF6' },
				label: {
					show: chartData.satisfactionRate?.length < 21,
					position: 'top',
					formatter: (params: any) => {
						const value = Number(params.value)
						return isNaN(value) ? '--' : `${Math.round(value)}%`
					}
				},
				smooth: true,
				yAxisIndex: 1
			}
		]
	}

	const toNum = (v: any) => {
		const n = Number(v)
		return isFinite(n) ? n : 0
	}

	const scoreList = [
		/** 满意度评分 */
		{ value: formatValue(toNum(summary?.avg_satisfaction_score)) },
		/** 满意率 */
		{ value: formatValue(toNum(summary?.satisfaction_rate)) },
		/** 低分率 */
		{ value: formatValue(toNum(summary?.low_score_rate)) }
	]

	return (
		<div className="bg-white rounded-lg overflow-hidden px-4 w-[49.5%]">
			<div className="mt-[18px]">
				<div className="text-[14px] flex items-center justify-between mb-[12px]">
					<div className="font-medium text-[#000]">{t('a_s_satisfaction_title_1')}</div>
					<Button
						className="px-[20px]"
						type="primary"
						loading={exportLoading}
						onClick={handleExport}
					>
						{t('qr_batch_download')}
					</Button>
				</div>

				<div className="flex w-full text-[#464646] text-[14px]">
					{scoreList.map((item, index) => {
						const config = Object?.values(textCsatMapping.satisfaction_trend)[index]
						return (
							<div key={index} className="flex-1">
								<div className="flex">
									<PopoverItem title={config.title} titleTooltip={config} />
								</div>

								<div className="text-[20px] font-bold text-[#000]">
									{loading ? (
										<Skeleton.Input active size="small" />
									) : (
										<DataSkeleton value={item.value} showPercent={index !== 0} />
									)}
								</div>
							</div>
						)
					})}
				</div>
			</div>

			<ReactECharts
				theme="theme_jekka"
				option={optionList}
				showLoading={loading}
				style={{ height: 350, width: '100%' }}
			/>
		</div>
	)
}

export default SatisfiedTrend
